export interface TalentRecord {
  id: string
  name: string
  role: string
  portfolio?: string
  whatsapp?: string
  email?: string
}

export interface TalentDeskResponse {
  talents: TalentRecord[]
  sheets: TalentDeskSheet[]
  loadedAt: string
  sourceName: string
}

export interface TalentDeskSheet {
  id: string
  name: string
  talents: TalentRecord[]
}

function normalizedHeader(value: unknown) {
  return String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '')
}

function firstColumn(headers: Array<string | undefined>, aliases: string[]) {
  return headers.findIndex(value => {
    const header = normalizedHeader(value)
    return Boolean(header) && aliases.some(alias => header === alias || header.includes(alias))
  })
}

function cell(row: unknown[], index: number) {
  return index >= 0 ? String(row[index] ?? '').trim() : ''
}

export function safeExternalUrl(value: string) {
  const candidate = value.trim()
  if (!candidate) return undefined
  try {
    const url = new URL(/^https?:\/\//i.test(candidate) ? candidate : `https://${candidate}`)
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : undefined
  } catch {
    return undefined
  }
}

export function emailLink(value: string) {
  const email = value.match(/[\w.!#$%&'*+/=?^`{|}~-]+@[\w-]+(?:\.[\w-]+)+/i)?.[0]
  return email ? { value: email, href: `mailto:${email}` } : undefined
}

export function whatsappLink(value: string) {
  const explicitUrl = safeExternalUrl(value)
  if (explicitUrl) {
    const hostname = new URL(explicitUrl).hostname.toLowerCase()
    if (hostname === 'wa.me' || hostname.endsWith('.whatsapp.com') || hostname === 'whatsapp.com') {
      return { value: explicitUrl, href: explicitUrl }
    }
  }
  let digits = value.replace(/\D/g, '')
  if (digits.length === 10 || digits.length === 11) digits = `55${digits}`
  if (digits.length < 10 || digits.length > 15) return undefined
  return { value: `+${digits}`, href: `https://wa.me/${digits}` }
}

export function parseTalentSheetValues(values: unknown[][]): TalentRecord[] {
  if (!Array.isArray(values) || !values.length) return []
  const aliases = {
    name: ['nome', 'nomecompleto', 'nomedocandidato', 'candidato', 'talento', 'profissional'],
    role: ['cargo', 'cargoatual', 'cargoalmejado', 'funcao', 'profissao', 'especialidade', 'areadeatuacao', 'area'],
    portfolio: ['portfolio', 'portifolio', 'linkdoportfolio', 'linkportfolio', 'site', 'behance'],
    contact: ['contato', 'contact', 'dadosdecontato'],
    whatsapp: ['whatsapp', 'telefone', 'celular', 'fone'],
    email: ['email', 'correioeletronico'],
  }
  const headerCandidates = values.slice(0, 50).map((row, index) => {
    const headers = Array.isArray(row) ? Array.from({ length: row.length }, (_, column) => normalizedHeader(row[column])) : []
    const columns = {
      name: firstColumn(headers, aliases.name),
      role: firstColumn(headers, aliases.role),
      portfolio: firstColumn(headers, aliases.portfolio),
      contact: firstColumn(headers, aliases.contact),
      whatsapp: firstColumn(headers, aliases.whatsapp),
      email: firstColumn(headers, aliases.email),
    }
    const score = (columns.name >= 0 ? 5 : 0) + Object.entries(columns).filter(([key, column]) => key !== 'name' && column >= 0).length
    return { index, columns, score }
  })
  const detectedHeader = headerCandidates.sort((left, right) => right.score - left.score || left.index - right.index)[0]
  const hasHeader = Boolean(detectedHeader && detectedHeader.columns.name >= 0)
  const fallbackStart = values.findIndex(row => Array.isArray(row) && row.filter(value => String(value ?? '').trim()).length >= 2)
  if (!hasHeader && fallbackStart < 0) return []
  const columns = hasHeader ? detectedHeader.columns : { name: 0, role: 1, portfolio: 2, contact: 3, whatsapp: -1, email: 4 }
  const firstDataRow = hasHeader ? detectedHeader.index + 1 : fallbackStart

  // A planilha insere os candidatos mais recentes no topo; por isso a ordem
  // física das linhas é mantida durante toda a conversão.
  return values.slice(firstDataRow).flatMap((row, index) => {
    if (!Array.isArray(row)) return []
    const name = cell(row, columns.name)
    if (!name) return []
    const contact = cell(row, columns.contact)
    const rawEmail = cell(row, columns.email) || contact
    const rawWhatsapp = cell(row, columns.whatsapp) || contact.replace(emailLink(contact)?.value ?? '', '')
    return [{
      id: `${firstDataRow + index + 1}-${name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`,
      name,
      role: cell(row, columns.role) || 'Cargo não informado',
      portfolio: safeExternalUrl(cell(row, columns.portfolio)),
      whatsapp: whatsappLink(rawWhatsapp)?.href,
      email: emailLink(rawEmail)?.value,
    }]
  })
}

export function buildTalentDeskSheets(worksheets: Array<{ name: string; values: unknown[][] }>): TalentDeskSheet[] {
  return worksheets.map((worksheet, index) => {
    const sheetName = String(worksheet.name || `Aba ${index + 1}`)
    const sheetId = `${index + 1}-${sheetName.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'aba'}`
    return {
      id: sheetId,
      name: sheetName,
      talents: parseTalentSheetValues(worksheet.values).map(talent => ({ ...talent, id: `${sheetId}-${talent.id}` })),
    }
  })
}

export function canAccessTalentDesk(profile: string) {
  return profile === 'Administrador' || profile === 'Gerente'
}
